"use client";
import type { FC, PropsWithChildren } from "react";
import { useEffect, useRef } from "react";

import { configureNewStore, ReduxStoreProvider } from "@jti/store";
import { magentoApiSlice, normalizeStoreConfigResponse } from "@jti/store/slices/magentoApiSlice";
import type { Store } from "@jti/store/types";

import type { MagentoBootstrapData } from "@/app/_internal/getMagentoBootstrapData";

import { buildCartPreloadedState } from "./utils/buildCartPreloadedState";
import { buildPreloadedState } from "./utils/buildPreloadedState";

type StoreProviderProps = {
  magentoApiBootstrapData?: MagentoBootstrapData;
};

export const StoreProvider: FC<PropsWithChildren<StoreProviderProps>> = ({ magentoApiBootstrapData, children }) => {
  const storeRef = useRef<Store | null>(null);

  if (!storeRef.current) {
    storeRef.current = configureNewStore(buildPreloadedState(buildCartPreloadedState(magentoApiBootstrapData)));
  }

  useEffect(() => {
    if (!magentoApiBootstrapData?.storeConfig) return;

    storeRef.current?.dispatch(
      magentoApiSlice.util.upsertQueryEntries([
        {
          endpointName: "getStoreConfig",
          arg: undefined,
          value: normalizeStoreConfigResponse(magentoApiBootstrapData.storeConfig),
        },
      ])
    );
  }, [magentoApiBootstrapData?.storeConfig]);

  return <ReduxStoreProvider store={storeRef.current}>{children}</ReduxStoreProvider>;
};
